import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { UserToInvite } from "@/App/components/InviteUsersDropdown/UserToInvite";
import { type DataState, InitialState, Loading, LoadingFailed, LoadingSucceeded } from "@/Pages/utils/DataState";
import { DispatchService } from "./dispatch.service";
import type { CandidateEmsUser } from "./CandidateEmsUser";
import type { TwiageRequestError } from "@/App/services/httpClient/HttpClient";

type DispatchStoreState = {
  actions: {
    getState: DataState<CandidateEmsUser[]>;
    sendState: DataState<CandidateEmsUser[]>;
  };
  users: UserToInvite[];
};

type CandidateUser = {
  id: string;
  displayName: string;
  isCandidate: boolean;
};

const candidateUsers: Array<CandidateUser> = [];

export const candidateAsToInvite = (candidate: CandidateEmsUser): UserToInvite => {
  return {
    id: candidate.id,
    displayName: candidate.displayName,
    invited: candidate.isCandidate,
    teams: [],
  };
};

export const useDispatchStore = defineStore("dispatch", () => {
  const state = ref<DispatchStoreState>({
    actions: {
      getState: new InitialState(),
      sendState: new InitialState(),
    },
    users: [],
  });

  const useAction = async <T>(
    action: () => Promise<T>,
    loader: keyof DispatchStoreState["actions"],
    error: string,
  ): Promise<T | null> => {
    state.value.actions[loader] = new Loading();
    try {
      const response = await action();
      state.value.actions[loader] = new LoadingSucceeded({ data: response });
      return response;
    } catch (e) {
      state.value.actions[loader] = new LoadingFailed((e as TwiageRequestError).message, error);
      return null;
    }
  };

  const getUsers = async ({ caseId, caseOwnerId }: { caseId: string; caseOwnerId?: string }): Promise<void> => {
    const candidateEmsUsers: CandidateEmsUser[] | null = await useAction(
      () => DispatchService.getUsers({ caseId }),
      "getState",
      "Failed to load EMS dispatch users",
    );

    state.value.users =
      candidateEmsUsers?.map((candidate) => ({
        ...candidateAsToInvite(candidate),
        isOwner: candidate.id === caseOwnerId,
      })) ?? [];
  };

  const getUser = ({ userId }: { userId: string }): string | undefined => {
    return candidateUsers.find((user) => user.id === userId)?.displayName;
  };

  const selectUserAsCandidate = async ({ caseId, userIds }: { caseId: string; userIds: string[] }): Promise<void> => {
    const response = await useAction(
      () => DispatchService.selectUserAsCandidate({ caseId, userIds }),
      "sendState",
      "Failed to invite EMS user(s) to case",
    );
    if (response) {
      state.value.users = state.value.users.map((user) => ({
        ...user,
        isInvited: user.invited || userIds.includes(user.id),
        toBeInvited: false,
        isOwner: user.isOwner,
      }));
    }
  };

  const removeUserAsCandidate = async ({ caseId, userIds }: { caseId: string; userIds: string[] }): Promise<void> => {
    const response = await useAction(
      () => DispatchService.removeUserAsCandidate({ caseId, userIds }),
      "sendState",
      "Failed to remove EMS user(s) as candidates from case",
    );
    if (response) {
      state.value.users = state.value.users.map((user) => ({
        ...user,
        isInvited: userIds.includes(user.id) ? false : user.invited,
        toBeInvited: false,
        isOwner: user.isOwner,
      }));
    }
  };

  return {
    getState: computed(() => state.value.actions.getState),
    sendState: computed(() => state.value.actions.sendState),
    users: computed(() => state.value.users),
    getUsers,
    getUser,
    selectUserAsCandidate,
    removeUserAsCandidate,
  };
});
